import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import { CustomDropdown, Option } from './CustomDropdown';

export interface BaseSelectorProps {
  /** The label to display in the selector */
  label: string;
  /** List of available options */
  options: Option[];
  /** Whether multiple selection is allowed */
  isMulti?: boolean;
  /** Optional list of options to exclude */
  excludeOptions?: string[];
  /** Callback for selection changes */
  onChange?: (selected: string[]) => void;
  /** Whether to show Select All option (only applies when isMulti is true) */
  allowSelectAll?: boolean;
  /** Whether the selector is disabled */
  disabled?: boolean;
  /** The size of the selector */
  size?: 'small' | 'medium';
  /** Initial selected values */
  value?: string[];
  /** Width of the selector button */
  width?: string | number;
  /** Tooltip text to show on hover */
  tooltip?: string;
  /** Function to format the option label */
  optionLabel?: (option: Option) => string | React.ReactNode;
  /** Function to format the selected label */
  selectedLabel?: (selected: Option) => string | React.ReactNode;
  /** Whether to show the label on top like a fieldset */
  topLabel?: boolean;
  /** Data test id for the selector */
  dataTestId?: string;
}

export const BaseSelector: React.FC<BaseSelectorProps> = ({
  label,
  options: initialOptions,
  isMulti = false,
  excludeOptions = [],
  onChange,
  allowSelectAll = true,
  disabled = false,
  size = 'medium',
  value: externalValue,
  width = '100%',
  tooltip,
  optionLabel,
  selectedLabel,
  topLabel,
  dataTestId,
}) => {
  const [selected, setSelected] = useState<string[]>([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // Filter out excluded options
  const options: Option[] = initialOptions.filter(
    (option) => !excludeOptions.includes(option.value),
  );

  const handleChange = (newSelected: string[]) => {
    setSelected(newSelected);
    onChange?.(newSelected);
  };

  const handleOpenChange = (open: boolean) => {
    setIsDropdownOpen(open);
  };

  useEffect(() => {
    if (externalValue) {
      setSelected(externalValue);
    }
  }, [externalValue]);

  const selector = (
    <Box sx={{ width }}>
      <CustomDropdown
        label={label}
        options={options}
        value={selected}
        onChange={handleChange}
        multiple={isMulti}
        allowSelectAll={allowSelectAll}
        disabled={disabled}
        size={size}
        width={width}
        optionLabel={optionLabel}
        selectedLabel={selectedLabel}
        topLabel={topLabel}
        handleOpenChange={handleOpenChange}
      />
    </Box>
  );

  return tooltip ? (
    <Tooltip
      title={tooltip}
      placement="left-start"
      disableHoverListener={isDropdownOpen}
      componentsProps={{
        tooltip: {
          sx: {
            fontSize: '0.85rem',
            maxWidth: '300px',
            padding: '8px 12px',
            lineHeight: 1.4,
          },
        },
      }}
    >
      <Box sx={{ width }} data-testid={dataTestId}>
        {selector}
      </Box>
    </Tooltip>
  ) : (
    selector
  );
};
